if (!customElements.get('bundle-product')) {
  class BundleProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.optionNames = (this.dataset.optionNames || '').split('||').filter(Boolean);
      this.incompleteMultiComponents = new Set();
      // Source of truth for each multi-dimension component's picks, kept in
      // JS memory because the theme's AJAX variant resolve re-renders the
      // whole variant-picker block on every change, regenerating these local
      // (non-Shopify) dimension inputs from scratch with nothing checked.
      this.multiDimensionState = {};

      this.bindMultiDimensionComponents();
      this.refreshState();

      // Real single-selection Shopify variants always represent a complete,
      // purchasable combination, so the generic path here only keeps the
      // single-dimension "Your Bundle" rows in sync with whichever variant
      // the theme's own variant-picker / product-info.js resolved.
      this.unsubscribe = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (event) => {
        if (event.data.sectionId !== this.sectionId) return;
        const resync = () => {
          // The section markup (including our multi-dimension cards and the
          // Your Bundle / progress rows) may have just been regenerated by
          // product-info.js -- re-bind to whatever is live now (refreshing
          // this.summaryRows in the process) and restore the user's picks +
          // our own disabled/incomplete state on top of it.
          this.bindMultiDimensionComponents();
          this.updateSummary(event.data.variant);
          this.refreshState();
        };
        // product-info.js swaps in the freshly-fetched section markup via a
        // transition (insert new / fade+remove old) that is still in flight
        // when this event fires -- measured up to ~1s later in practice --
        // so resync now and again a few times after to win against it
        // regardless of exactly how long that transition takes.
        resync();
        [150, 400, 800, 1300].forEach((delay) => setTimeout(resync, delay));
      });

      const initialScript = this.querySelector('variant-picker [data-selected-variant]');
      if (initialScript) {
        try {
          this.updateSummary(JSON.parse(initialScript.textContent));
        } catch (e) {
          /* no initial variant to show yet */
        }
      }
    }

    disconnectedCallback() {
      this.unsubscribe && this.unsubscribe();
    }

    bindMultiDimensionComponents() {
      this.summaryRows = Array.from(this.querySelectorAll('[data-summary-row]'));
      this.progressEl = this.querySelector('[data-bundle-progress]');

      const cards = Array.from(this.querySelectorAll('[data-bundle-multi-component]'));

      cards.forEach((card) => {
        const componentIndex = card.dataset.componentIndex;
        const dimensionCount = parseInt(card.dataset.dimensionCount, 10) || 0;
        const dimensionGroups = Array.from(card.querySelectorAll('.bundle-card__option-group[data-dimension-index]'));
        const proxyInputs = Array.from(card.querySelectorAll('.bundle-card__native-proxy .bundle-card__option-input'));

        // A dimension with only one valid value left is not a real choice --
        // bundle-component-card.liquid renders it as fixed text with no
        // input, carrying its resolved value on data-fixed-value instead.
        const fixedValues = dimensionGroups.map((g) => (g.dataset.fixedValue !== undefined ? g.dataset.fixedValue : null));
        const hasRealDimension = fixedValues.some((v) => v === null);

        if (!this.multiDimensionState[componentIndex]) {
          this.multiDimensionState[componentIndex] = fixedValues.slice();
        }
        const savedValues = this.multiDimensionState[componentIndex];

        // Restore the user's previous picks onto the (possibly freshly
        // regenerated) local inputs. Fixed dimensions have no input to
        // restore onto -- their value already comes from data-fixed-value.
        savedValues.forEach((value, i) => {
          if (value === null || fixedValues[i] !== null) return;
          const input = dimensionGroups[i] && Array.from(dimensionGroups[i].querySelectorAll('.bundle-card__dimension-input')).find((el) => el.value === value);
          if (input) input.checked = true;
        });

        if (!hasRealDimension || savedValues.every((v) => v !== null)) {
          this.incompleteMultiComponents.delete(componentIndex);
        } else {
          this.incompleteMultiComponents.add(componentIndex);
        }
        this.updateMultiSummaryRow(componentIndex, savedValues, dimensionGroups);

        const onChange = (event) => {
          // These local dimension inputs are not real Shopify options, but
          // they still live inside <variant-picker>, which listens for ANY
          // bubbling "change" event (not just ones from recognized option
          // inputs) and immediately kicks off a server resolve. Stop it here
          // so an incomplete (e.g. Size-only) pick never triggers a
          // premature fetch. The real, bubbling "change" this drives is
          // dispatched further down on the hidden native proxy input, once
          // the combination is whole.
          event.stopPropagation();

          const values = [];
          for (let i = 0; i < dimensionCount; i++) {
            if (fixedValues[i] !== null) {
              values[i] = fixedValues[i];
              continue;
            }
            const checked = dimensionGroups[i] && dimensionGroups[i].querySelector('.bundle-card__dimension-input:checked');
            values[i] = checked ? checked.value : null;
          }
          this.multiDimensionState[componentIndex] = values;

          const complete = values.every((v) => v !== null);
          if (complete) {
            const joined = values.join(' — ');
            const match = proxyInputs.find((input) => input.value === joined);
            if (match && !match.checked) {
              match.checked = true;
              match.dispatchEvent(new Event('change', { bubbles: true }));
            }
            this.incompleteMultiComponents.delete(componentIndex);
          } else {
            this.incompleteMultiComponents.add(componentIndex);
          }

          this.updateMultiSummaryRow(componentIndex, values, dimensionGroups);
          this.refreshState();
        };

        dimensionGroups.forEach((group) => {
          group.querySelectorAll('.bundle-card__dimension-input').forEach((input) => {
            if (input.dataset.bound) return;
            input.dataset.bound = 'true';
            input.addEventListener('change', onChange);
          });
        });
      });
    }

    updateMultiSummaryRow(componentIndex, values, dimensionGroups) {
      const row = this.summaryRows.find((r) => r.dataset.summaryComponentIndex === componentIndex);
      if (!row) return;
      const valueEl = row.querySelector('[data-summary-value]');
      if (!valueEl) return;

      const missingIndex = values.findIndex((v) => v === null);
      if (missingIndex === -1) {
        valueEl.textContent = ' (' + values.join(' & ') + ')';
        row.dataset.complete = 'true';
      } else {
        const dimensionName = (dimensionGroups[missingIndex] && dimensionGroups[missingIndex].dataset.dimensionName) || 'option';
        valueEl.textContent = ' (Choose ' + dimensionName.toLowerCase() + ')';
        row.dataset.complete = 'false';
      }
    }

    refreshState() {
      const disabled = this.incompleteMultiComponents.size > 0;
      this.getSubmitButtons().forEach((btn) => {
        btn.disabled = disabled;
      });
      // Buy it now (Shopify's dynamic checkout button) shares the same
      // underlying variant as Add to Cart, so it must not be usable while a
      // multi-dimension component is still incomplete either.
      document.querySelectorAll('.bundle-builder__dynamic-checkout, .m-sticky-addtocart .m-product-dynamic-checkout').forEach((el) => {
        el.classList.toggle('bundle-builder__dynamic-checkout--disabled', disabled);
      });
      this.updateProgress();
    }

    getSubmitButtons() {
      const buttons = [];
      const main = this.querySelector('.m-add-to-cart');
      if (main) buttons.push(main);
      const sticky = document.getElementById('Sticky-Addtocart-' + this.sectionId);
      if (sticky) {
        const stickyBtn = sticky.querySelector('.m-add-to-cart');
        if (stickyBtn) buttons.push(stickyBtn);
      }
      return buttons;
    }

    updateProgress() {
      if (!this.progressEl) return;
      const total = parseInt(this.progressEl.dataset.total, 10) || 0;
      if (!total) return;
      const complete = total - this.incompleteMultiComponents.size;
      this.progressEl.textContent = complete + ' / ' + total + ' selected';
      this.progressEl.dataset.complete = complete === total ? 'true' : 'false';
    }

    updateSummary(variant) {
      if (!variant || !this.optionNames.length) return;
      const values = String(variant.title).split(' / ');

      this.optionNames.forEach((name, index) => {
        const row = (this.summaryRows || []).find((r) => r.dataset.summaryOptionName === name && r.dataset.summaryMultiDimension === undefined);
        if (!row) return;
        const valueEl = row.querySelector('[data-summary-value]');
        if (valueEl && values[index]) valueEl.textContent = ' (' + values[index] + ')';
      });
    }
  }

  customElements.define('bundle-product', BundleProduct);
}
